import { creatingHelpers } from "global/common/creating";
import { filterHelpers } from "global/common/filtering";
import { gEnums } from "global/enums/globalEnums";
import { settingsIconTypes } from "global/enums/settingsIconTypes";
import { useContext, useEffect, useState } from "react";
import { _showAppLogs, LogContext } from "../LogContext";
import { PapsContext } from "../PapsContext";
import { useAppUserAccess } from "./useAppUserAccess";

const _docName = 'baseSettingsAll'
const _allowAll = false
const _ammendedTypes = ['_position', 'dividedText', 'hideFromMenu', 'triggerUrl', 'allowSettingsUpdate', 'settingsUpdateHandler', 'settingsUpdateHandlerFunction', 'settingsUpdateAutomatic', 'settingsUpdateDescription']

export const useBaseSettingsArea = ({ bsa }) => {

  // logContext
  const logContext = useContext(LogContext)
  const { log_handlers } = logContext ?? {}

  // appUserContext 
  const appUserAccess = useAppUserAccess()
  const { accessLevel, isDataAdmin } = appUserAccess ?? {}

  const papsContext = useContext(PapsContext);
  const { paps_state } = papsContext
  const { appArea, pathViews } = paps_state ?? {}

  const [baseSettingsArea, setBaseSettingsArea] = useState()

  useEffect(() => {

    if (!isDataAdmin || !bsa) return;

    const getBaseSettingsArea = async () => {

      const bs = _.cloneDeep(bsa)
      const bsc = getCombinedSettings(bs)
      const { baseSettings_ammended, saSplits, childMenuItems } = getAmmendedBaseSettings(bs, bsc, appArea, accessLevel, pathViews)
      const baseSettings_appArea = {
        baseSettings: bs,
        baseSettings_combined: bsc,
        baseSettings_ammended,
        saSplits,
        childMenuItems
      }
      _showAppLogs.baseSettings && log_handlers.addLog(`♻️⚙️⚙️ Loaded data (${_docName}) from bsa`)
      // await cacheDataManager.setGroup(cache_groupType, baseSettings_appArea)
      setBaseSettingsArea(baseSettings_appArea);
    }

    getBaseSettingsArea()

  }, [bsa, appArea, isDataAdmin]);

  return { baseSettingsArea };
};

const getCombinedSettings = (baseSettings) => {

  const useCombined = true

  const normalItems = ['global', 'other', 'page', 'pageItem', 'prop', 'propSection']

  const { componentPermissions, adjustedPermissions } = baseSettings
  const _permissions = adjustedPermissions ? adjustedPermissions : componentPermissions
  const { group } = _permissions ?? {}
  const _combined = {}

  Object.keys(baseSettings).forEach(key => {
    if (normalItems.includes(key)) {
      _combined[key] = {}
      const baseSetting = _.cloneDeep(baseSettings[key])
      const permissionSetting = _permissions[key]
      Object.keys(baseSetting).forEach(bsKey => {
        const baseSettingItem = baseSetting[bsKey]
        const permissionSettingItem = permissionSetting ? permissionSetting[bsKey] : null
        const _cbm = { ...baseSettingItem, ...permissionSettingItem }
        const combined = useCombined ? _cbm : baseSettingItem
        _combined[key][bsKey] = combined
      })
    }
  })

  _combined['group'] = {}

  if (group) {
    Object.keys(group).forEach(groupKey => {
      _combined['group'][groupKey] = group[groupKey]
    })
  }
  return _combined
}

const getAmmendedBaseSettings = (baseSettings, baseSettings_combined, appArea, accessLevel, pathViews, adjustedPermissions_s) => {

  // const { componentPermissions, adjustedPermissions } = baseSettings
  // const _adjustedPermissions = adjustedPermissions_s ? adjustedPermissions_s : adjustedPermissions

  // _permissions are the current permissions for the entire app
  // const _permissions = _adjustedPermissions ? _adjustedPermissions : componentPermissions

  const allChildItems = []

  const saSplits = ammendPageItemBaseSplits(baseSettings_combined, appArea, accessLevel, pathViews)

  // add the groupItems to each of the groups
  ammendAllowedGroupItemsForGroups(saSplits.group, baseSettings_combined, appArea, accessLevel, pathViews)

  const sci = {}
  allChildItems.sort().forEach(ci => {
    sci[ci] = { caption: _.startCase(ci) }
  })

  // baseSettings_ammended, saSplits, childMenuItems  
  return { baseSettings_ammended: baseSettings, saSplits, childMenuItems: sci }

}

const ammendAllowedGroupItemsForGroups = (group, baseSettings_combined, appArea, accessLevel, pathViews) => {

  const _groupSortProp = 'settingsMenuGroupType'

  if (group) {

    Object.keys(group).forEach(groupKey => {

      const groupItem = group[groupKey]

      const combinedSetting = baseSettings_combined[groupKey]

      const isGlobal = groupKey === 'global' ? true : false

      if (groupItem) {
        Object.keys(groupItem).forEach(groupItemKey => {

          const _groupIteme = _.pickBy(combinedSetting, function (n) {
            if (n) { return (n[_groupSortProp] === groupItemKey) }
          })

          const _groupItems_allowed = filterAllowedGroupItems(groupItemKey, _groupIteme, appArea, isGlobal, accessLevel, pathViews)

          // ammend the dividedText and triggerUrl
          Object.keys(_groupItems_allowed).forEach(k => {

            const _groupItemee = _groupIteme[k]

            _ammendedTypes.forEach(ats => {
              if (_groupItemee[ats]) {
                _groupItems_allowed[k][ats] = _groupItemee[ats]
              }
            })

          })

          Object.keys(_groupItems_allowed).forEach(k => {
            const _groupItem = _groupIteme[k]
            _ammendedTypes.forEach(ats => {
              if (_groupItem[ats]) {
                _groupItems_allowed[k][ats] = _groupItem[ats]
              }
            })
          })

          Object.keys(_groupItems_allowed).forEach(k => {
            ammendCaptionAndIcon(_groupItems_allowed[k], appArea, k)
          })

          groupItem[groupItemKey].groupItems = _groupItems_allowed
        })
      }
    })
  }
}

const ammendPageItemBaseSplits = (baseSettings_combined, appArea, accessLevel, pathViews) => {

  // these are the core settings with all the attibutes. This has nothing to do with any reduced/filtered items
  const { group, global, page, pageItem, prop, propSection } = baseSettings_combined ? baseSettings_combined : {}

  // create the splits object
  const saSplits = {
    group: { global: {}, page: {} },
    page: { global: {}, page: {} },
    global: { global: {}, page: {} },
    pageItem: { global: {}, page: {} },
    prop: { global: {}, page: {} },
    propSection: { global: {}, page: {} },
  }

  // filter by appArea, accessLevel and pathViews, NOT pageArea
  const allowed = {
    group: filterAllowed_aaapv(group, appArea, accessLevel, pathViews),
    global: filterAllowed_aaapv(global, appArea, accessLevel, pathViews),
    page: filterAllowed_aaapv(page, appArea, accessLevel, pathViews),
    pageItem: filterAllowed_aaapv(pageItem, appArea, accessLevel, pathViews),
    prop: filterAllowed_aaapv(prop, appArea, accessLevel, pathViews),
    propSection: filterAllowed_aaapv(propSection, appArea, accessLevel, pathViews),
  }

  const favs = _.filter(allowed.global, { includeInFavorites: true })

  // allowed does NOT contain groupItems at this point 

  Object.keys(allowed).forEach(allowedKey => {
    splitItemByPageAreaType(saSplits[allowedKey], allowed[allowedKey])
  })

  const _authGroups = getAuthGroups(saSplits.group, appArea)

  getGroupItemsForSplits(saSplits, appArea)

  return {
    ...saSplits,
    authGroups: _authGroups,
  }

}

const getGroupItemsForSplits = (saSplits, appArea) => {
  Object.keys(saSplits).forEach(spKey => {
    switch (spKey) {
      case 'pageItem':
      case 'prop':
      case 'propSection':
        const split = saSplits[spKey]
        Object.keys(split).forEach(spiKey => { // global or page
          const spItem = split[spiKey]
          Object.keys(spItem).forEach(spItemKey => { // item
            const spItemItem = spItem[spItemKey]
            const { groupItems } = spItemItem
            if (groupItems) {
              const _groupItems = ammendGroupItems(groupItems, spiKey === 'global', appArea, spItemKey)
              spItemItem.groupItems = _groupItems
            }
          })
        })
        break;
      default:
      // nothing
    }
  })
}

const ammendGroupItems = (groupItems, isGlobal, appArea, spItemKey) => {
  const _allowed = {}
  const _excluded = {}
  if (groupItems) {
    Object.keys(groupItems).forEach(k => {
      const groupItem = groupItems[k]
      const { componentAreas } = groupItem ?? {}
      const { appAreaTypes, pageAreaTypes } = componentAreas ?? {}
      let allowed = true

      if (appAreaTypes && appAreaTypes.length > 0) {
        if (!appAreaTypes.includes('all')) {
          if (!appAreaTypes.includes(appArea)) {
            allowed = false
          }
        }
      }
      if (pageAreaTypes && pageAreaTypes.length > 0) {
        if (!pageAreaTypes.includes('all')) {
          if (isGlobal) {
            if (!pageAreaTypes.includes('global')) {
              allowed = false
            }
          } else {
            if (!pageAreaTypes.includes('page')) {
              allowed = false
            }
          }
        }
      }
      if (!allowed) {
        _excluded[k] = groupItems[k]
      } else {
        _allowed[k] = groupItems[k]
      }
    })
  }

  return _allowed

}

const getAuthGroups = (split_group, appArea) => {

  const _authGroups = { global: {}, page: {} }

  if (split_group) {
    Object.keys(split_group).forEach(groupKey => {
      const group = split_group[groupKey]
      creatingHelpers.createKeys(group)
      const grps = _.groupBy(group, 'settingsAuthLevel')
      Object.keys(grps).forEach(key => {
        const grp = grps[key]
        const grpo = {}
        grp.forEach(gr => {
          ammendCaptionAndIcon(gr, appArea)
          grpo[gr.key] = gr
        })
        const authLevel = filterHelpers.find(gEnums.authLevels, 'value', parseInt(key))
        const groupIconColor = authLevel ? authLevel.color : iconColorTypes.menuItem
        _authGroups[groupKey][key] = {
          groupedItems: grpo,
          iconColor: groupIconColor
        }
      })
    })
  }

  return _authGroups

}

const ammendCaptionAndIcon = (item, appArea, k) => {

  const { key, caption, icon, appAs, appAsNull, appUserAsNull } = item ?? {}
  const _key = key ? key : k
  if (!caption) { item.caption = _.startCase(_key) }
  if (!icon) { item.icon = settingsIconTypes[_key] ? settingsIconTypes[_key] : 'cog' }

  const _appUserAsNull = k ? item.caption.startsWith('App User ') : false
  const _appAsNull = k && !_appUserAsNull ? item.caption.startsWith('App ') : false

  if (appUserAsNull || _appUserAsNull) {
    const _caption = _key.replace('appUser', '')
    item.caption = _.startCase(_caption).trim()
  }

  if (appAsNull || _appAsNull) {
    const _caption = _key.replace('app', '')
    item.caption = _.startCase(_caption).trim()
  }

  if (appAs) {
    const _caption = _key.replace('app', _.startCase(appArea)).trim()
    item.caption = _.startCase(_caption).trim()
  }
}


/**
 * Splits the item into global and page
 * @param {object} splitItem 
 * @param {object} items 
 */
const splitItemByPageAreaType = (splitItem, items) => {
  const pa = { global: {}, page: {} }
  Object.keys(items).forEach(k => {
    const miItem = items[k]
    const { componentAreas } = miItem
    const { pageAreaTypes } = componentAreas ?? {}
    // loop the pa
    Object.keys(pa).forEach(paKey => {
      if (pageAreaTypes && pageAreaTypes.length > 0) {
        if (_allowAll || pageAreaTypes.includes('all') || pageAreaTypes.includes(paKey)) {
          switch (paKey) {
            case 'global':
              splitItem.global[k] = _.cloneDeep(miItem)
              break;
            case 'page':
              splitItem.page[k] = _.cloneDeep(miItem)
              break;
            default:
            // nothing
          }
        }
      } else {
        splitItem.global[k] = _.cloneDeep(miItem)
        splitItem.page[k] = _.cloneDeep(miItem)
      }
    })
  })
}

/**
 * 
 * @param {object} items 
 * @param {string} appArea 
 * @param {number} accessLevel 
 * @param {object} pathViews_current 
 * @returns a list of allowed items
 * @description looks at the appAreaTypes, settingsAuthLevel and pathViews of each of the items componentAreas to see if the item is allowed.
 */
const filterAllowed_aaapv = (items, appArea, accessLevel, pathViews_current) => {
  const _allowedItems = {}
  const _nonAllowedItems = {}
  const _disallowedKeys = ['none']
  if (items) {
    Object.keys(items).forEach(key => {
      const item = items[key]
      if (isAllowed_aaapv(item, appArea, accessLevel, pathViews_current) && !_disallowedKeys.includes(key)) {
        _allowedItems[key] = item
      } else {
        _nonAllowedItems[key] = item
      }
    })
  }
  return _allowedItems
}


/**
 * 
 * @param {object} item 
 * @param {string} appArea 
 * @param {number} authLevel 
 * @param {object} pathViews_current 
 * @returns true if the item is allowed
 * @description looks at the appAreaTypes, settingsAuthLevel and pathViews of the componentAreas to see if the item is allowed
 */
const isAllowed_aaapv = (item, appArea, authLevel, pathViews_current) => {

  let allowed = true

  const { componentAreas, settingsAuthLevel, isPrivateSetting, clientOnlyKeys, eventOnlyKeys } = item ?? {}
  const { appAreaTypes, pathViews } = componentAreas ?? {}

  if (settingsAuthLevel) {
    if (authLevel < settingsAuthLevel) {
      allowed = false
    }
  }

  if (isPrivateSetting) {
    allowed = false
  }

  if (clientOnlyKeys) {
    if (pathViews_current.clients) {
      allowed = clientOnlyKeys.includes(pathViews_current.clients)
    } else {
      allowed = false
    }
  }

  if (eventOnlyKeys) {
    if (pathViews_current.events) {
      allowed = eventOnlyKeys.includes(pathViews_current.events)
    } else {
      allowed = false
    }
  }

  // if (!allowed && eventAreaKeys) {
  //   if (pathViews_current.events) {
  //     allowed = eventOnlyKeys && eventOnlyKeys.includes(pathViews_current) ? true : false
  //   } else {
  //     allowed = false
  //   }
  // }

  if (!allowed && appAreaTypes && appAreaTypes.length > 0) {
    if (!appAreaTypes.includes('all')) {
      if (!appAreaTypes.includes(appArea)) {
        allowed = false
      }
    }
  }

  if (pathViews) {
    if (!allowed && !pathViews.includes('all')) {
      if (!pathViews_current || !pathViews_current[pathViews]) {
        allowed = false
      }
    }
  }

  return _allowAll ? true : allowed
}

const filterAllowedGroupItems = (groupKey, items, appArea, isGlobal, authLevel, pathViews_current) => {
  const _disallowedKeys = ['none']
  const _allowedItems = {}
  const _nonAllowedItems = {}
  if (items) {
    Object.keys(items).forEach(key => {
      const item = items[key]
      if (isAllowedAll(item, appArea, isGlobal, authLevel, pathViews_current) && !_disallowedKeys.includes(key)) {
        _allowedItems[key] = item
      } else {
        _nonAllowedItems[key] = item
      }
    })
  }
  return _allowedItems
}

const isAllowedAll = (item, appArea, isGlobal, authLevel, pathViews_current) => {

  let allowed = true


  const { componentAreas, settingsAuthLevel, eventOnlyKeys, eventAreaKey, eventAreaKeys } = item ?? {}
  const { appAreaTypes, pageAreaTypes, pathViews } = componentAreas ?? {}

  // if (eventAreaKey || eventAreaKeys) {
  //   console.log('eventAreaKey', eventAreaKey, item)
  // }

  if (settingsAuthLevel) {
    if (authLevel < settingsAuthLevel) {
      allowed = false
    }
  }

  if (eventOnlyKeys) {
    if (pathViews_current.events) {
      allowed = eventOnlyKeys.includes(pathViews_current.events)
    } else {
      allowed = false
    }
  }

  if (!allowed && eventAreaKey) {
    if (pathViews_current.events) {
      allowed = eventAreaKey === pathViews_current.events
    } else {
      allowed = false
    }
  }

  if (appAreaTypes && appAreaTypes.length > 0) {
    if (!appAreaTypes.includes('all')) {
      if (!appAreaTypes.includes(appArea)) {
        allowed = false
      }
    }
  }

  if (pageAreaTypes && pageAreaTypes.length > 0) {
    if (!pageAreaTypes.includes('all')) {
      if (isGlobal) {
        if (!pageAreaTypes.includes('global')) {
          allowed = false
        }
      } else {
        if (!pageAreaTypes.includes('page')) {
          allowed = false
        }
      }
    }
  }

  if (pathViews) {
    if (!pathViews.includes('all')) {
      if (!pathViews_current || !pathViews_current[pathViews]) {
        allowed = false
      }
    }
  }

  return allowed
}
